import { Shield, Lock, Eye, Database, Users, Globe, Bell, Settings, FileText, Mail, Phone, MapPin, AlertTriangle, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";

const COMPANY_INFO = {
  name: "FonderX Currency Exchange Inc.",
  address: "2-723 12th Street, New Westminster, BC V3M 4J8",
  phone: "+1 (778) 759-0400",
  email: "privacy@fonderx.com",
  website: "www.fonderx.com"
};

const sections = [
  {
    id: "introduction",
    title: "1. Introduction",
    icon: Shield,
    content: `${COMPANY_INFO.name} ("FonderX," "we," "us," or "our") is committed to protecting your privacy and the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our currency exchange services, visit our website, or interact with us in any way.

This Privacy Policy complies with the Personal Information Protection and Electronic Documents Act (PIPEDA), the Personal Information Protection Act (PIPA) of British Columbia, and other applicable privacy laws.

By using our services, you consent to the collection, use, and disclosure of your personal information as described in this Privacy Policy.`
  },
  {
    id: "collection",
    title: "2. Information We Collect",
    icon: Database,
    content: `**Personal Information:**
We collect personal information that you provide to us, including:

• **Identity Information:** Full legal name, date of birth, government-issued ID numbers (driver's license, passport, etc.)
• **Contact Information:** Address, email address, phone number
• **Financial Information:** Bank account details, transaction history, source of funds
• **Identification Documents:** Copies of government-issued ID, proof of address documents
• **Biometric Information:** Photographs for identity verification purposes

**Transaction Information:**
• Currency exchange transactions and amounts
• Cryptocurrency wallet addresses
• Payment method details
• Transaction dates and times

**Technical Information:**
• IP address and device information
• Browser type and version
• Website usage data and cookies
• Login credentials (encrypted)

**Additional Information:**
• Occupation and employer information (for compliance purposes)
• Purpose of transactions
• Communication records with our staff`
  },
  {
    id: "use",
    title: "3. How We Use Your Information",
    icon: Settings,
    content: `We use your personal information for the following purposes:

**Service Delivery:**
• Processing currency exchange transactions
• Verifying your identity
• Providing customer support
• Managing your account

**Legal Compliance:**
• Complying with FINTRAC reporting requirements
• Anti-money laundering (AML) and Know Your Customer (KYC) procedures
• Responding to legal requests and regulatory requirements
• Preventing fraud and financial crimes

**Business Operations:**
• Improving our services and customer experience
• Analyzing transaction patterns and trends
• Training our staff
• Internal record keeping

**Communication:**
• Sending transaction confirmations and receipts
• Providing updates about our services
• Responding to your inquiries
• Marketing communications (with your consent)

We will only use your personal information for the purposes for which it was collected, unless we obtain your consent or are required or permitted by law to use it for other purposes.`
  },
  {
    id: "disclosure",
    title: "4. Information Sharing and Disclosure",
    icon: Users,
    content: `We may share your personal information in the following circumstances:

**Regulatory Bodies:**
• FINTRAC (Financial Transactions and Reports Analysis Centre of Canada)
• Law enforcement agencies when required by law
• Tax authorities when required

**Service Providers:**
• Identity verification services
• IT and security service providers
• Professional advisors (lawyers, accountants)

**Business Transfers:**
In the event of a merger, acquisition, or sale of assets, your information may be transferred to the new owner.

**Legal Requirements:**
We may disclose your information when required by law, court order, or regulatory request.

**With Your Consent:**
We may share your information with third parties when you have given us explicit consent to do so.

**We Do NOT:**
• Sell your personal information to third parties
• Share your information with marketing companies
• Use your information for targeted advertising without consent`
  },
  {
    id: "retention",
    title: "5. Data Retention",
    icon: FileText,
    content: `We retain your personal information for as long as necessary to fulfill the purposes for which it was collected, including:

**Transaction Records:**
• Minimum 5 years as required by FINTRAC
• May be retained longer for legal or regulatory purposes

**Identity Documents:**
• Retained for the duration of our business relationship
• Plus 5 years after the relationship ends

**Communication Records:**
• Generally retained for 3 years
• May be retained longer if related to disputes or complaints

**Account Information:**
• Active accounts: As long as the account is active
• Inactive accounts: 7 years after last activity

After the retention period expires, we will securely destroy or anonymize your personal information.`
  },
  {
    id: "security",
    title: "6. Data Security",
    icon: Lock,
    content: `We implement appropriate technical and organizational measures to protect your personal information:

**Technical Safeguards:**
• Encryption of data in transit and at rest
• Secure socket layer (SSL) technology
• Firewalls and intrusion detection systems
• Regular security assessments and updates

**Organizational Safeguards:**
• Employee training on data protection
• Access controls and authentication
• Background checks for employees handling sensitive data
• Incident response procedures

**Physical Safeguards:**
• Secure storage of physical documents
• Controlled access to our premises
• CCTV surveillance
• Secure document destruction

While we strive to protect your information, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security.

**Breach Notification:**
In the event of a data breach that poses a real risk of significant harm, we will notify affected individuals and the Privacy Commissioner as required by law.`
  },
  {
    id: "cookies",
    title: "7. Cookies and Tracking Technologies",
    icon: Globe,
    content: `Our website uses cookies and similar tracking technologies:

**Essential Cookies:**
• Required for website functionality
• Session management
• Security features

**Analytics Cookies:**
• Help us understand website usage
• Improve user experience
• Track page views and navigation

**Preferences Cookies:**
• Remember your settings
• Language preferences
• Display preferences

**Managing Cookies:**
You can control cookies through your browser settings:
• Block all cookies
• Delete existing cookies
• Allow only certain cookies

Note: Blocking cookies may affect website functionality.

**Do Not Track:**
We respect Do Not Track browser settings and will not track users who have enabled this feature.`
  },
  {
    id: "rights",
    title: "8. Your Privacy Rights",
    icon: Eye,
    content: `Under Canadian privacy law, you have the following rights:

**Right to Access:**
You can request access to the personal information we hold about you. We will respond within 30 days.

**Right to Correction:**
You can request correction of inaccurate or incomplete information.

**Right to Deletion:**
You can request deletion of your personal information, subject to legal retention requirements.

**Right to Withdraw Consent:**
You can withdraw consent for non-essential data processing at any time.

**Right to Complain:**
You can file a complaint with the Office of the Privacy Commissioner of Canada or the BC Information and Privacy Commissioner.

**How to Exercise Your Rights:**
Contact our Privacy Officer at:
• Email: ${COMPANY_INFO.email}
• Phone: ${COMPANY_INFO.phone}
• Mail: ${COMPANY_INFO.address}

We may require identity verification before processing your request.`
  },
  {
    id: "marketing",
    title: "9. Marketing Communications",
    icon: Bell,
    content: `**Opt-In Marketing:**
We only send marketing communications with your explicit consent.

**Types of Communications:**
• Newsletter and rate updates
• Promotional offers
• New service announcements
• Educational content

**Opting Out:**
You can unsubscribe from marketing communications at any time by:
• Clicking the "unsubscribe" link in emails
• Contacting us directly
• Updating your account preferences

**Transactional Communications:**
We will continue to send essential transactional communications (receipts, confirmations, security alerts) even if you opt out of marketing.

**We Do NOT:**
• Send spam or unsolicited commercial emails
• Share your email with third-party marketers
• Use aggressive marketing tactics`
  },
  {
    id: "children",
    title: "10. Children's Privacy",
    content: `Our services are not intended for individuals under 18 years of age. We do not knowingly collect personal information from children.

If we learn that we have collected personal information from a child under 18, we will take steps to delete that information promptly.

If you believe we have inadvertently collected information from a child, please contact us immediately.`
  },
  {
    id: "international",
    title: "11. International Data Transfers",
    content: `Your personal information is primarily processed and stored in Canada.

**When We Transfer Data:**
In some cases, we may transfer data to service providers located outside Canada for:
• Cloud storage services
• Identity verification services
• Technical support

**Safeguards:**
When transferring data internationally, we ensure:
• Adequate data protection measures are in place
• Contractual protections with service providers
• Compliance with Canadian privacy laws

**Countries:**
Data may be transferred to:
• United States (for certain service providers)
• European Union (for identity verification)

These countries may have different privacy laws than Canada.`
  },
  {
    id: "changes",
    title: "12. Changes to This Policy",
    content: `We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors.

**How We Notify You:**
• Posting the updated policy on our website
• Email notification for significant changes
• Notice at our physical location

**Effective Date:**
Changes become effective when posted unless otherwise stated.

**Your Continued Use:**
Your continued use of our services after changes indicates acceptance of the updated policy.

**Previous Versions:**
We maintain records of previous versions of this policy. Contact us if you need access to a previous version.`
  }
];

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen" data-testid="privacy-policy-page">
      {/* Hero Section */}
      <section className="hero-gradient py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
              <Lock className="w-6 h-6 text-[#3B82F6]" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white">
                Privacy Policy
              </h1>
              <p className="text-blue-100/60 text-sm mt-1">Last Updated: February 2025</p>
            </div>
          </div>
          <p className="text-lg text-blue-100/70 leading-relaxed">
            Your privacy is important to us. This policy explains how we collect, use, and protect your personal information.
          </p>
        </div>
      </section>

      {/* Quick Summary */}
      <section className="bg-[#001a40] py-8 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-lg font-semibold text-white mb-4">Privacy at a Glance</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-start gap-3 p-4 bg-white/5 rounded-lg">
              <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white text-sm font-medium">We protect your data</p>
                <p className="text-blue-100/50 text-xs">Industry-standard security measures</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-white/5 rounded-lg">
              <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white text-sm font-medium">We don't sell your data</p>
                <p className="text-blue-100/50 text-xs">Your information is never sold</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-white/5 rounded-lg">
              <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white text-sm font-medium">You're in control</p>
                <p className="text-blue-100/50 text-xs">Access, correct, or delete your data</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="bg-[#002055] py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Table of Contents */}
          <div className="mb-12 p-6 bg-white/5 rounded-xl border border-white/10">
            <h3 className="text-white font-semibold mb-4">Table of Contents</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {sections.map((section, idx) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="text-blue-100/60 hover:text-[#3B82F6] text-sm transition-colors py-1"
                >
                  {idx + 1}. {section.title.split('. ')[1]}
                </a>
              ))}
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-12">
            {sections.map((section) => (
              <div key={section.id} id={section.id} className="scroll-mt-32">
                <div className="flex items-center gap-3 mb-4">
                  {section.icon && (
                    <div className="w-10 h-10 rounded-lg bg-[#3B82F6]/20 flex items-center justify-center flex-shrink-0">
                      <section.icon className="w-5 h-5 text-[#3B82F6]" />
                    </div>
                  )}
                  <h2 className="text-xl md:text-2xl font-bold text-white">
                    {section.title}
                  </h2>
                </div>
                <div className="text-blue-100/70 leading-relaxed space-y-4 whitespace-pre-line pl-0 md:pl-13">
                  {section.content.split('\n\n').map((paragraph, idx) => (
                    <p key={idx} className="text-sm md:text-base">
                      {paragraph.split('**').map((text, i) => 
                        i % 2 === 1 ? <strong key={i} className="text-white">{text}</strong> : text
                      )}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Contact Information */}
          <div className="mt-16 p-8 bg-white/5 rounded-2xl border border-white/10">
            <h3 className="text-xl font-bold text-white mb-6">Privacy Officer Contact</h3>
            <p className="text-blue-100/70 mb-6">
              For privacy-related questions, requests, or complaints, please contact our Privacy Officer:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#3B82F6]" />
                <a href={`mailto:${COMPANY_INFO.email}`} className="text-blue-100/70 hover:text-white transition-colors">
                  {COMPANY_INFO.email}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#3B82F6]" />
                <a href={`tel:${COMPANY_INFO.phone.replace(/\s/g, '')}`} className="text-blue-100/70 hover:text-white transition-colors">
                  {COMPANY_INFO.phone}
                </a>
              </div>
              <div className="flex items-start gap-3 md:col-span-2">
                <MapPin className="w-5 h-5 text-[#3B82F6] flex-shrink-0 mt-0.5" />
                <span className="text-blue-100/70">{COMPANY_INFO.address}</span>
              </div>
            </div>
            
            <div className="mt-6 pt-6 border-t border-white/10">
              <p className="text-blue-100/50 text-sm">
                <strong className="text-white">Regulatory Complaints:</strong> If you are not satisfied with our response, 
                you may file a complaint with the Office of the Privacy Commissioner of Canada at{" "}
                <a href="https://www.priv.gc.ca" target="_blank" rel="noopener noreferrer" className="text-[#3B82F6] hover:underline">
                  www.priv.gc.ca
                </a>
              </p>
            </div>
          </div>

          {/* Footer Links */}
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 pt-8 border-t border-white/10">
            <Link to="/terms-of-service" className="text-[#3B82F6] hover:underline text-sm">
              View our Terms of Service →
            </Link>
            <Link to="/contact" className="text-blue-100/60 hover:text-white text-sm transition-colors">
              Have questions? Contact us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
